import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
    Card,
    CardHeader,
    CardContent,
    Avatar,
    FormControlLabel,
    Checkbox,
} from '@material-ui/core';
import { UPDATE_VALIDATION_RULE, ADD_ROLLING_PERIOD_RULE, REMOVE_ROLLING_PERIOD_RULE_AT_INDEX } from '../../../store/ruleSet';
import InputTitle from '../InputTitle';
import StyledSwitch from '../StyledSwitch';
import MaxHoursPerRollingPeriod from './MaxHoursPerRollingPeriod';
import MaxHoursPerRollingPeriodRulesSummary from './MaxHoursPerRollingPeriodRulesSummary';
import ActualHours from './ActualHours';
import { RuleSetType } from '../../../../Enums';
import './Step3-ScheduleRulesSection.scss';
import OverrideDuration from './OverrideDuration';

/**
 * @param {{ isDisabled: boolean, validationErrors: {[Key: string], isDisabled: boolean}, stepNumber: number  }}
 */
export default function ScheduleRulesSection(props) {
    const { isDisabled, validationErrors, stepNumber } = props;
    const { t } = useTranslation('employmentBillboard', { keyPrefix: 'settings.ruleSet.scheduleRulesStep' });
    const dispatch = useDispatch();
    /** @type {EmploymentBillboard.AdminStore} */
    // @ts-ignore
    const { adminRuleSet, adminAppData } = useSelector((state) => state);
    const {
        isMaxHoursPerDay, isIncludeBreaksInMaxHoursPerDay, isUseActualHoursInMaxHoursPerDay, maxHoursPerDayBreakRule,
        isMaxHoursPerWeek, isIncludeBreaksInMaxHoursPerWeek, isUseActualHoursInMaxHoursPerWeek, maxHoursPerWeekBreakRule,
        isRestTimeBetweenShift, isUseActualHoursInRestTimeBetweenShift,
        isMaxHoursPerRollingPeriod, maxHoursInRollingPeriodRules, isUseActualHoursInMaxHoursInRollingPeriod, isIncludeBreaksInMaxHoursInRollingPeriod, maxHoursInRollingPeriodBreakRule,
        isMaxShiftsPerDay, isMaxConsecutiveDay, isOverrideMaxHoursPerWeek, isOverrideMaxHoursPerDay, overrideMaxHoursPerWeekInMinutes, overrideMaxHoursPerDayInMinutes,
    } = adminRuleSet.validationRuleSet;
    const breakRules = adminAppData.ruleSets.filter(x => x.ruleSetType === RuleSetType.BreakRule);
    /**
     * @param {string} target
     * @param {string} value
     */
    const updateValidationRulesStore = (target, value) => {
        let otherTargets = {};
        if (target === 'isMaxHoursPerRollingPeriod') {
            otherTargets = {
                maxHoursInRollingPeriodRules: [],
                isUseActualHoursInMaxHoursInRollingPeriod: false,
                isIncludeBreaksInMaxHoursInRollingPeriod: false,
                maxHoursInRollingPeriodBreakRule: null,
            }
        }
        else if(target === 'isMaxHoursPerDay'){
            otherTargets = {
                isIncludeBreaksInMaxHoursPerDay: false,
                isUseActualHoursInMaxHoursPerDay: false,
                maxHoursPerDayBreakRule: null,
                isOverrideMaxHoursPerDay: false,
                overrideMaxHoursPerDayInMinutes: null,
            }
        }
        else if(target === 'isMaxHoursPerWeek'){
            otherTargets = {
                isIncludeBreaksInMaxHoursPerWeek: false,
                isUseActualHoursInMaxHoursPerWeek: false,
                maxHoursPerWeekBreakRule: null,
                isOverrideMaxHoursPerWeek: false,
                overrideMaxHoursPerWeekInMinutes: null,
            }
        }
        else if(target === 'isRestTimeBetweenShift'){
            otherTargets = {
                isUseActualHoursInRestTimeBetweenShift: false,
            }
        }
        else if(target === 'isOverrideMaxHoursPerDay'){
            otherTargets = {
                isOverrideMaxHoursPerDay: value,
                overrideMaxHoursPerDayInMinutes: null,
            }
        }
        else if(target === 'isOverrideMaxHoursPerWeek'){
            otherTargets = {
                isOverrideMaxHoursPerWeek: value,
                overrideMaxHoursPerWeekInMinutes: null,
            }
        }
        else if(target === 'overrideMaxHoursPerDayInMinutes'){
            otherTargets = {
                overrideMaxHoursPerDayInMinutes: value,
            }
        }
        else if(target === 'overrideMaxHoursPerWeekInMinutes'){
            otherTargets = {
                overrideMaxHoursPerWeekInMinutes: value,
            }
        }
        dispatch({
            type: UPDATE_VALIDATION_RULE,
            payload: {
                [target]: value,
                ...otherTargets,
            },
        });
    };

    /**
     * 
     * @param {number} maxHoursInRollingPeriod 
     * @param {number} rollingPeriodNumber 
     * @param {RollingPeriodUnit} rollingPeriodUnit 
     */
    const onAddRollingRule = (maxHoursInRollingPeriod, rollingPeriodNumber, rollingPeriodUnit) => {
        dispatch({
            type: ADD_ROLLING_PERIOD_RULE,
            payload: {
                maxHoursInRollingPeriod, rollingPeriodNumber, rollingPeriodUnit,
            },
        });
    }

    /**
     * @param {number} index
     */
     const onDeleteRollingRule = (index) => {
        dispatch({
            type: REMOVE_ROLLING_PERIOD_RULE_AT_INDEX,
            payload: index,
        });
    };

    return (
        <Card className="card">
            <CardHeader
                className="card--header"
                gutterbottom
                avatar={<Avatar className="avatar">{stepNumber}</Avatar>}
                title="Schedule rules"
            />
            <CardContent className="no-gap">
                <InputTitle>{t('header')}</InputTitle>
                <div className={`${isMaxHoursPerDay ? 'open-sub-section' : ''}`}>
                    <StyledSwitch
                        checked={isMaxHoursPerDay}
                        onChange={(value) => updateValidationRulesStore('isMaxHoursPerDay', value)}
                        label={t('maxHoursPerDay.label')}
                        disabled={isDisabled}
                    />
                    {isMaxHoursPerDay && (
                        <div>
                            <FormControlLabel
                                control={(
                                    <Checkbox
                                        checked={isIncludeBreaksInMaxHoursPerDay}
                                        onClick={({ target: { checked: isChecked } }) => updateValidationRulesStore('isIncludeBreaksInMaxHoursPerDay', isChecked)}
                                    />
                                )}
                                label={t('breakRules.label')}
                                disabled={isDisabled}
                            />
                            <OverrideDuration 
                                label={t('overrideDuration.label')} 
                                placeholder={t('overrideDuration.placeholder')}
                                isOverride={isOverrideMaxHoursPerDay}
                                overrideValue={overrideMaxHoursPerDayInMinutes}
                                setIsOverride={(checked) => updateValidationRulesStore('isOverrideMaxHoursPerDay', checked)}
                                setOverrideValue={(value) => updateValidationRulesStore('overrideMaxHoursPerDayInMinutes', value)}
                                helpIconTooltip={t('overrideDuration.toolTip')}
                                isDisabled={isDisabled}
                                />
                            {
                                validationErrors.overrideMaxHoursPerDayInMinutes && isOverrideMaxHoursPerDay &&  overrideMaxHoursPerDayInMinutes <= 0 ? <span style={{ color: 'red' }}>{t('overrideDuration.error')}</span> : null
                            }
                            <ActualHours
                                isActualHours={isUseActualHoursInMaxHoursPerDay}
                                setIsActualHours={(checked) => updateValidationRulesStore('isUseActualHoursInMaxHoursPerDay', checked)}
                                breakRuleId={maxHoursPerDayBreakRule}
                                setBreakRuleId={(id) => updateValidationRulesStore('maxHoursPerDayBreakRule', id)}
                                breakRules={breakRules}
                                helpIconTooltip={t('maxHoursPerDay.isUseActualHoursInMaxHoursPerDayTooltip')}
                                isDisabled={isDisabled}
                            />
                        </div>
                    )}
                </div>
                <div className={`${isMaxHoursPerWeek ? 'open-sub-section' : ''}`}>
                    <StyledSwitch
                        checked={isMaxHoursPerWeek}
                        onChange={(value) => updateValidationRulesStore('isMaxHoursPerWeek', value)}
                        label={t('maxHoursPerWeek.label')}
                        disabled={isDisabled}
                    />
                    {isMaxHoursPerWeek && (
                        <div>
                            <FormControlLabel
                                control={(
                                    <Checkbox
                                        checked={isIncludeBreaksInMaxHoursPerWeek}
                                        onClick={({ target: { checked: isChecked } }) => updateValidationRulesStore('isIncludeBreaksInMaxHoursPerWeek', isChecked)}
                                    />
                                )}
                                label={t('breakRules.label')}
                                disabled={isDisabled}
                            />
                            <OverrideDuration 
                                label={t('overrideDuration.label')} 
                                placeholder={t('overrideDuration.placeholder')}
                                isOverride={isOverrideMaxHoursPerWeek}
                                overrideValue={overrideMaxHoursPerWeekInMinutes}
                                setIsOverride={(checked) => updateValidationRulesStore('isOverrideMaxHoursPerWeek', checked)}
                                setOverrideValue={(value) => updateValidationRulesStore('overrideMaxHoursPerWeekInMinutes', value)}
                                helpIconTooltip={t('overrideDuration.toolTip')}
                                isDisabled={isDisabled}
                                />
                            {
                                validationErrors.overrideMaxHoursPerWeekInMinutes && isOverrideMaxHoursPerWeek &&  overrideMaxHoursPerWeekInMinutes <= 0 ? <span style={{ color: 'red' }}>{t('overrideDuration.error')}</span> : null
                            }                                
                            <ActualHours
                                isActualHours={isUseActualHoursInMaxHoursPerWeek}
                                setIsActualHours={(checked) => updateValidationRulesStore('isUseActualHoursInMaxHoursPerWeek', checked)}
                                breakRuleId={maxHoursPerWeekBreakRule}
                                setBreakRuleId={(id) => updateValidationRulesStore('maxHoursPerWeekBreakRule', id)}
                                breakRules={breakRules}
                                helpIconTooltip={t('maxHoursPerWeek.isUseActualHoursInMaxHoursPerWeekTooltip')}
                                isDisabled={isDisabled}
                            />
                        </div>
                )}
                </div>
                <div className={`${isRestTimeBetweenShift ? 'open-sub-section' : ''}`}>
                    <StyledSwitch
                        checked={isRestTimeBetweenShift}
                        onChange={(value) => updateValidationRulesStore('isRestTimeBetweenShift', value)}
                        label={t('restTimeBetweenShift.label')}
                        disabled={isDisabled}
                    />
                    {isRestTimeBetweenShift && (
                        <ActualHours
                            isActualHours={isUseActualHoursInRestTimeBetweenShift}
                            setIsActualHours={(checked) => updateValidationRulesStore('isUseActualHoursInRestTimeBetweenShift', checked)}
                            isSelectBreakRuleId={false}
                            helpIconTooltip={t('restTimeBetweenShift.isUseActualHoursInRestTimeBetweenShiftTooltip')}
                            isDisabled={isDisabled}
                        />
                    )}
                </div>
                <StyledSwitch
                    checked={isMaxShiftsPerDay}
                    onChange={(value) => updateValidationRulesStore('isMaxShiftsPerDay', value)}
                    label={t('maxShiftsPerDay.label')}
                    disabled={isDisabled}
                />
                
                <div className={`${isMaxHoursPerRollingPeriod ? 'open-sub-section' : ''}`}>
                    <StyledSwitch
                        checked={isMaxHoursPerRollingPeriod}
                        onChange={(value) => updateValidationRulesStore('isMaxHoursPerRollingPeriod', value)}
                        label={t('rollingPeriodRules.label')}
                        disabled={isDisabled}
                    />
                    {
                        isMaxHoursPerRollingPeriod && (
                            <div>
                                <FormControlLabel
                                    control={(
                                        <Checkbox
                                            checked={isIncludeBreaksInMaxHoursInRollingPeriod}
                                            onClick={({ target: { checked: isChecked } }) => updateValidationRulesStore('isIncludeBreaksInMaxHoursInRollingPeriod', isChecked)}
                                        />
                                    )}
                                    label={t('breakRules.label')}
                                    disabled={isDisabled}
                                />
                                <ActualHours
                                    isActualHours={isUseActualHoursInMaxHoursInRollingPeriod}
                                    setIsActualHours={(checked) => updateValidationRulesStore('isUseActualHoursInMaxHoursInRollingPeriod', checked)}
                                    breakRuleId={maxHoursInRollingPeriodBreakRule}
                                    setBreakRuleId={(id) => updateValidationRulesStore('maxHoursInRollingPeriodBreakRule', id)}
                                    breakRules={breakRules}
                                    helpIconTooltip={t('rollingPeriodRules.isIncludeBreaksInMaxHoursInRollingPeriodTooltip')}
                                    isDisabled={isDisabled}
                                />
                                <MaxHoursPerRollingPeriod
                                    onAdd={onAddRollingRule}
                                    isDisabled={isDisabled || maxHoursInRollingPeriodRules.length >= 2}
                                />
                                {
                                    maxHoursInRollingPeriodRules && !!maxHoursInRollingPeriodRules.length && (
                                        <MaxHoursPerRollingPeriodRulesSummary
                                            onDelete={onDeleteRollingRule}
                                            maxHoursInRollingPeriodRules={maxHoursInRollingPeriodRules}
                                            isDisabled={isDisabled}
                                        />
                                    )
                                }
                                {
                                    validationErrors.isMaxHoursPerRollingPeriod ? <span style={{ color: 'red' }}>You must select at least one rolling period rule</span> : null
                                }
                            </div>
                        )
                    }
                </div>
			 <StyledSwitch
                    checked={isMaxConsecutiveDay}
                    onChange={(value) => updateValidationRulesStore('isMaxConsecutiveDay', value)}
                    label={t('maxConsecutiveDay.label')}
                    disabled={isDisabled}
                />
            </CardContent>
        </Card>

    );
}
